// src/registry/add-template.ts — add a provider from a builtin template

import { provisionProviderCredential, saveProviderCredential } from '../env.js';
import { credentialInstanceAuthRef } from '../credential-helper.js';
import { isSdkMigratedNpm } from '../provider-factory.js';
import { registerTraceSecret } from '../trace-log.js';
import type { ProviderTemplate } from '../provider-templates.js';
import { classifyFreeStatus, isFreeStatus } from '../free-models.js';
import {
  cancelCredentialDelete,
  journalCredentialWrite,
  queueCredentialDelete,
  reconcilePendingCredentialDeletes,
} from './credential-lifecycle.js';
import { fetchTemplateModels } from './fetch-template-models.js';
import { loadRegistryStrict, saveRegistry } from './io.js';
import {
  withCredentialMutationLock,
  withProviderMutationLock,
  withRegistryWriteLock,
} from './lock.js';
import {
  buildPricingIndex,
  enrichModelsWithPricing,
  enrichPricingAsync,
  loadPricingCache,
  pricingPlatformForProvider,
} from './pricing.js';
import { isProviderConfiguredForTemplate } from './resolve-template.js';
import type { RegistryProvider } from './types.js';

export interface AddTemplateResult {
  added: boolean;
  provider?: RegistryProvider;
  modelCount?: number;
  error?: string;
  hint?: string;
  credentialCleanupPending?: boolean;
  credentialCleanupReconciled?: boolean;
}

function existingProviderError(
  template: ProviderTemplate,
  existing: RegistryProvider | undefined,
  replaceExisting: boolean | undefined,
): AddTemplateResult | null {
  if (!existing) return null;
  const removeFirst = `Remove it first with: clodex providers remove ${existing.id}`;
  if (!replaceExisting || existing.id !== template.id) {
    return {
      added: false,
      error: `${template.name} is already configured.`,
      hint: removeFirst,
    };
  }
  if (existing.defaultAuthRef !== undefined
    || existing.activeAuthAccount !== undefined
    || Object.keys(existing.authAccounts ?? {}).length > 0) {
    // Template replacement publishes one credential lineage. It cannot safely
    // discard a selected slot, parked default, or named slots; the provider
    // removal lifecycle is the operation that journals every one of them.
    return {
      added: false,
      error: `${template.name} has OAuth account state and cannot be replaced in place.`,
      hint: removeFirst,
    };
  }
  return null;
}

async function probeTemplatePackage(template: ProviderTemplate): Promise<string | null> {
  if (!template.supported) return template.unsupportedReason ?? 'Provider is not supported yet.';
  if (!template.npm) return 'Template is missing an SDK package.';
  if (!isSdkMigratedNpm(template.npm) && template.npm !== '@ai-sdk/anthropic') {
    return `SDK package ${template.npm} is not available in clodex.`;
  }
  try {
    await import(template.npm);
    return null;
  } catch {
    return `Could not load ${template.npm}. Run npm install in your clodex checkout.`;
  }
}

function filterAnonymousFreeModels<T extends { cost?: { input: number; output: number }; isFree?: boolean; freeStatus?: ReturnType<typeof classifyFreeStatus> }>(
  models: T[],
  template: ProviderTemplate,
): T[] {
  if (!template.anonymousFreeModels) return models;
  return models.filter(model => isFreeStatus(classifyFreeStatus({
    model,
    providerId: template.id,
    templateId: template.id,
  })));
}

/** Test API key, persist credential + registry entry. */
export async function addProviderFromTemplate(
  template: ProviderTemplate,
  apiKey: string,
  opts?: { replaceExisting?: boolean; baseUrl?: string },
): Promise<AddTemplateResult> {
  const packageError = await probeTemplatePackage(template);
  if (packageError) {
    return { added: false, error: packageError };
  }

  const trimmedKey = apiKey.trim();
  if (!trimmedKey && !template.apiKeyOptional) {
    return { added: false, error: 'API key cannot be empty.' };
  }

  const existingState = await withRegistryWriteLock(() => {
    const registry = loadRegistryStrict();
    const existing = registry.providers.find(provider =>
      isProviderConfiguredForTemplate(provider, template.id));
    const error = existingProviderError(template, existing, opts?.replaceExisting);
    if (error) {
      return {
        error,
      };
    }
    return { authRef: existing?.authRef ?? null, error: null };
  });
  if (existingState.error) return existingState.error;

  // Registered before the first authenticated request rather than inside
  // fetchTemplateModels, which runs after the probe: nothing leaks today (the
  // probe logs nothing and its error string is a fixed literal), but the
  // invariant worth holding is "registered before first use", not "registered
  // before the first call that happens to log".
  if (trimmedKey) registerTraceSecret(trimmedKey);

  // The probe destination comes from the template alone — `verifyCredential`
  // takes no base URL — so `opts.baseUrl` deliberately does not reach it. A
  // caller-supplied address must never receive a live credential.
  if (trimmedKey && template.verifyCredential) {
    const credentialError = await template.verifyCredential(trimmedKey);
    if (credentialError) {
      return { added: false, error: credentialError };
    }
  }

  const fetched = await fetchTemplateModels(template, trimmedKey, opts?.baseUrl);
  if (fetched.error || fetched.models.length === 0) {
    return {
      added: false,
      error: fetched.error ?? 'No models returned.',
      hint: fetched.hint,
    };
  }
  const usableModels = !trimmedKey && template.anonymousFreeModels
    ? filterAnonymousFreeModels(fetched.models, template)
    : fetched.models;
  if (usableModels.length === 0) {
    return {
      added: false,
      error: 'No free models were returned for anonymous access.',
      hint: template.signupUrl ? `Add a ${template.name} API key from ${template.signupUrl} to use paid models.` : undefined,
    };
  }

  const pricingCache = loadPricingCache();
  const platform = pricingPlatformForProvider(template.id, template.id);
  const discoveredModels = usableModels.map(m => ({
    ...m,
    apiUrl: m.apiUrl ?? fetched.baseUrl,
  }));
  const pricedModels = template.preserveModelPricing
    ? discoveredModels
    : enrichModelsWithPricing(discoveredModels, buildPricingIndex(pricingCache), platform);
  const account = `provider:${template.id}`;
  const result: AddTemplateResult = await withProviderMutationLock(template.id, async () => {
    const currentState = await withRegistryWriteLock(() => {
      const registry = loadRegistryStrict();
      const existing = registry.providers.find(provider =>
        isProviderConfiguredForTemplate(provider, template.id));
      const error = existingProviderError(template, existing, opts?.replaceExisting);
      if (error) {
        return {
          existingAuthRef: null,
          error,
        };
      }
      return { existingAuthRef: existing?.authRef ?? null, error: null };
    });
    if (currentState.error) return currentState.error;

    const authRef = trimmedKey ? credentialInstanceAuthRef(account) : 'none:anonymous';
    const persistAndCommit = async (): Promise<AddTemplateResult> => {
      if (trimmedKey) {
        await journalCredentialWrite(authRef);
        const saved =
          currentState.existingAuthRef === authRef
            ? await saveProviderCredential(authRef, trimmedKey)
            : await provisionProviderCredential(authRef, trimmedKey);
        if (!saved) {
          return {
            added: false,
            error: 'Could not save API key to the credential store.',
            hint: 'Check credential-store access and try again.',
          };
        }
      }

      return withRegistryWriteLock(async () => {
        const registry = loadRegistryStrict();
        const existing = registry.providers.find(provider =>
          isProviderConfiguredForTemplate(provider, template.id));
        const existingError = existingProviderError(template, existing, opts?.replaceExisting);
        if (existingError) return existingError;
        if ((existing?.authRef ?? null) !== currentState.existingAuthRef) {
          return {
            added: false,
            error: `${template.name} changed while its credential was being saved.`,
            hint: 'Retry the provider update.',
          };
        }

        const now = new Date().toISOString();
        const entry: RegistryProvider = {
          id: template.id,
          templateId: template.id,
          name: template.name,
          enabled: true,
          authRef,
          authType: trimmedKey ? template.authType : 'none',
          ...(template.preserveModelPricing ? { preserveModelPricing: true } : {}),
          ...(!trimmedKey && template.anonymousFreeModels
            ? { subscriptionFilter: 'free' as const }
            : {}),
          api: {
            npm: template.npm,
            url: fetched.baseUrl,
          },
          addedAt: existing?.addedAt ?? now,
          refreshedAt: now,
          modelsCache: {
            fetchedAt: now,
            models: pricedModels,
          },
        };

        if (existing) {
          const idx = registry.providers.findIndex(p => p.id === template.id);
          registry.providers[idx] = entry;
        } else {
          registry.providers.push(entry);
        }
        if (existing?.authRef && existing.authRef !== authRef) {
          await queueCredentialDelete(existing.authRef);
        }
        saveRegistry(registry);
        let credentialCleanupPending = false;
        if (trimmedKey) {
          try {
            await cancelCredentialDelete(authRef);
          } catch {
            credentialCleanupPending = true;
          }
        }
        return {
          added: true,
          provider: entry,
          modelCount: pricedModels.length,
          ...(credentialCleanupPending ? { credentialCleanupPending: true } : {}),
        };
      });
    };

    if (!trimmedKey) return persistAndCommit();
    return withCredentialMutationLock(authRef, persistAndCommit);
  });

  if (result.added) {
    try {
      const cleanup = await reconcilePendingCredentialDeletes();
      result.credentialCleanupPending =
        cleanup.pending.length > 0 || cleanup.persistenceError !== undefined;
    } catch {
      result.credentialCleanupPending = true;
    }
  } else {
    try {
      const cleanup = await reconcilePendingCredentialDeletes();
      result.credentialCleanupPending =
        cleanup.pending.length > 0 || cleanup.persistenceError !== undefined;
    } catch {
      result.credentialCleanupPending = true;
    }
  }
  result.credentialCleanupReconciled = true;

  if (result.added) enrichPricingAsync();
  return result;
}
